import React from 'react';
import { Shield, Database, Eye, Share2, Lock, Clock, UserCheck, Cookie, Baby, RefreshCw, Mail, Phone } from 'lucide-react';
import Navbar from '../components/navbar';
import Footer from '../components/footer';

const SectionCard = ({ icon: Icon, number, title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 transition-all duration-300 hover:shadow-md">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-[#1A87CF]/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#1A87CF]" />
      </div>
      <h2 className="text-lg sm:text-xl font-bold text-slate-900 font-rubik">
        {number}. {title}
      </h2>
    </div>
    <div className="text-slate-600 text-sm sm:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const SubSection = ({ title, items }) => (
  <div>
    <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
    <ul className="list-disc list-inside space-y-1 text-slate-600 ml-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  </div>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <Navbar scrollToSection={() => {}} />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1A87CF] to-[#1565a0] text-white pt-28 sm:pt-32 pb-12 sm:pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/15 mb-5">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-rubik mb-4">Privacy Policy</h1>
          <p className="text-white/80 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            ABN Networking ("ABN", "we", "our", "us") is the controller of your personal data. This Privacy Policy explains how we collect, use, process, and protect your personal information when you use our mobile application, website, or related services ("Services").
          </p>
          <p className="text-white/60 text-xs sm:text-sm mt-4">
            We encourage you to read this Privacy Policy carefully to understand how your data is handled when using ABN Networking.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-6">

        {/* 1. What Personal Data We Collect */}
        <SectionCard icon={Database} number={1} title="What Personal Data We Collect">
          <p>We may collect the following types of personal data depending on your usage:</p>

          <SubSection
            title="a. Identification & Contact Data"
            items={[
              'Name, username',
              'Email address',
              'Phone number',
              'Business name and address',
              'Profile information',
            ]}
          />

          <SubSection
            title="b. Business & Account Data"
            items={[
              'Business category and services',
              'Networking activity (meetings, referrals, business given/received)',
              'Membership details and chapter information',
            ]}
          />

          <SubSection
            title="c. Usage & Technical Data"
            items={[
              'Device type, browser, and OS',
              'IP address (approximate location)',
              'App usage data (pages viewed, features used)',
              'Session duration and crash logs',
            ]}
          />

          <SubSection
            title="d. Communication Data"
            items={[
              'Messages, requests, or feedback shared within the app',
              '"Ask & Give" posts and interactions',
              'Profile content such as images or descriptions',
            ]}
          />

          <SubSection
            title="e. Sensitive Data (Optional)"
            items={[
              'We do not require sensitive personal data. If you voluntarily provide such information, it will only be used for the specific purpose with your consent.',
            ]}
          />
        </SectionCard>

        {/* 2. How We Collect Your Data */}
        <SectionCard icon={Eye} number={2} title="How We Collect Your Data">
          <p>We collect your data in the following ways:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>Directly from you when you register, create a profile, or use features</li>
            <li>Automatically through app usage, analytics, and device data</li>
            <li>From other members when they refer or connect with you</li>
            <li>From service providers (e.g., hosting, analytics, payment services)</li>
          </ul>
        </SectionCard>

        {/* 3. How We Use Your Data */}
        <SectionCard icon={Database} number={3} title="How We Use Your Data">
          <p>We use your personal data to:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>Create and manage your account</li>
            <li>Enable networking between members</li>
            <li>Display your business profile to other members</li>
            <li>Provide features like map-based member discovery</li>
            <li>Track networking performance (meetings, referrals, business growth)</li>
            <li>Improve app functionality and user experience</li>
            <li>Communicate updates, notifications, and support</li>
            <li>Ensure security and prevent misuse</li>
          </ul>
          <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-800 text-sm font-medium">
            We do not sell your personal data to third parties.
          </div>
        </SectionCard>

        {/* 4. Member Visibility & Networking */}
        <SectionCard icon={Eye} number={4} title="Member Visibility & Networking">
          <p>ABN Networking is a community-based platform, and certain information is visible to other members:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>Name, business details, and contact information</li>
            <li>Profile information and services offered</li>
            <li>Networking activities (limited to platform features)</li>
          </ul>
          <p className="mt-2 text-slate-500 italic text-sm">You can manage what information you share through your profile settings.</p>
        </SectionCard>

        {/* 5. Data Sharing */}
        <SectionCard icon={Share2} number={5} title="Data Sharing">
          <p>We may share your data only in the following cases:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>With service providers (hosting, analytics, messaging tools)</li>
            <li>With ABN members for networking purposes</li>
            <li>For legal compliance if required by law</li>
            <li>During business transfers (merger, acquisition, etc.)</li>
          </ul>
          <p className="mt-2 font-medium text-slate-700">All partners are required to protect your data securely.</p>
        </SectionCard>

        {/* 6. Data Security */}
        <SectionCard icon={Lock} number={6} title="Data Security">
          <p>We implement appropriate technical and organizational measures such as:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
            {['Data Encryption', 'Secure Servers', 'Access Control', 'Regular Monitoring'].map((item) => (
              <div key={item} className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center text-sm font-medium text-slate-700">
                {item}
              </div>
            ))}
          </div>
          <p className="mt-3">to protect your personal data from unauthorized access or misuse.</p>
        </SectionCard>

        {/* 7. Data Retention */}
        <SectionCard icon={Clock} number={7} title="Data Retention">
          <p>We retain your data:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>As long as your account is active</li>
            <li>As needed to provide services</li>
            <li>To comply with legal obligations</li>
          </ul>
          <p className="mt-2">You may request deletion of your data at any time.</p>
        </SectionCard>

        {/* 8. Your Rights */}
        <SectionCard icon={UserCheck} number={8} title="Your Rights">
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>Access your personal data</li>
            <li>Update or correct your data</li>
            <li>Request deletion of your account</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p className="mt-2 text-slate-500 text-sm">To exercise your rights, contact us at the details below.</p>
        </SectionCard>

        {/* 9. Cookies & Tracking */}
        <SectionCard icon={Cookie} number={9} title="Cookies & Tracking">
          <p>We may use cookies or similar technologies to:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>Keep you logged in</li>
            <li>Improve app performance</li>
            <li>Analyze usage trends</li>
          </ul>
          <p className="mt-2 text-slate-500 text-sm">You can control these settings through your device or browser.</p>
        </SectionCard>

        {/* 10. Children's Privacy */}
        <SectionCard icon={Baby} number={10} title="Children's Privacy">
          <p>ABN Networking is not intended for individuals under the age of 18. We do not knowingly collect data from minors.</p>
        </SectionCard>

        {/* 11. Changes to This Policy */}
        <SectionCard icon={RefreshCw} number={11} title="Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. Updates will be posted within the app or website.</p>
        </SectionCard>

        {/* 12. Contact Us */}
        <div className="bg-gradient-to-r from-[#1A87CF] to-[#1565a0] rounded-2xl p-6 sm:p-8 text-white">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold font-rubik">12. Contact Us</h2>
          </div>
          <p className="text-white/80 text-sm sm:text-base mb-5">
            If you have any questions about this Privacy Policy, you can contact us:
          </p>
          <div className="space-y-3">
            <p className="font-bold text-lg font-rubik">ABN – Ahir Business Network</p>
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-white/70 shrink-0" />
              <span className="text-white/90 text-sm">abndynamic@gmail.com</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-white/70 shrink-0" />
              <span className="text-white/90 text-sm">+91 88666 68942</span>
            </div>
          </div>
        </div>

      </div>

      <Footer scrollToSection={() => {}} />
    </div>
  );
};

export default PrivacyPolicy;
